import { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CategoryIconRenderer } from '@/features/product-library/utils/categoryIcons';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { CategoryTemplate } from '@/services/categoryTemplateService';
import {
  useMergedCategoriesHierarchy,
  type MergedCategory,
  type DomainType,
} from '@/features/product-library/hooks/useMergedCategories';
import { Plus } from 'lucide-react';
import { QuickCategoryCreateDialog } from '@/components/portal/QuickCategoryCreateDialog';
import { logger } from '@/lib/logger';

export type ProductCategory = string; // Now dynamic from database

export interface CategorySelection {
  category: string;
  subcategory?: string;
}

interface CategoryDropdownProps {
  value?: CategorySelection | string | null;
  onChange: (selection: CategorySelection | null) => void;
  isAutoApplied?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  layout?: 'vertical' | 'horizontal';
  displayMode?: 'full' | 'icon' | 'auto';
  showLabels?: boolean;
  productType?: 'lighting_fixtures' | 'lv_products' | 'lighting_controls' | 'all';
  /** Allow creating new categories. Default: true for lv_products, false for others */
  allowCategoryCreation?: boolean;
  /** Allow creating new subcategories. Default: true for lv_products/lighting_fixtures, false for controls */
  allowSubcategoryCreation?: boolean;
}

export function CategoryDropdown({
  value,
  onChange,
  isAutoApplied = false,
  disabled = false,
  className,
  placeholder = 'Select Category',
  layout = 'vertical',
  displayMode = 'full',
  showLabels = false,
  productType = 'all',
  allowCategoryCreation,
  allowSubcategoryCreation,
}: CategoryDropdownProps) {
  // Determine effective creation permissions based on product type
  // Default: LV products allow category creation, fixtures/controls don't
  const canCreateCategory =
    allowCategoryCreation ?? (productType === 'lv_products' || productType === 'all');

  // Default: Controls never allow subcategory creation, others do
  const canCreateSubcategory = allowSubcategoryCreation ?? productType !== 'lighting_controls';
  const queryClient = useQueryClient();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');

  // State for create category dialogs
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [createSubcategoryOpen, setCreateSubcategoryOpen] = useState(false);
  const [createDomain, setCreateDomain] = useState<DomainType>('lighting_fixtures');

  // Determine effective display mode
  const effectiveDisplayMode =
    displayMode === 'auto' ? (layout === 'horizontal' ? 'icon' : 'full') : displayMode;

  // Fetch merged categories (system + organization custom) using the hierarchy hook
  const { hierarchy: lightingHierarchy, data: lightingCategories = [] } =
    useMergedCategoriesHierarchy('lighting_fixtures');
  const { hierarchy: lvHierarchy, data: lvCategories = [] } =
    useMergedCategoriesHierarchy('lv_products');
  const { hierarchy: controlsHierarchy, data: controlsCategories = [] } =
    useMergedCategoriesHierarchy('lighting_controls');

  // Convert MergedCategory to CategoryTemplate format for backward compatibility
  const convertToTemplate = (mc: MergedCategory): CategoryTemplate => ({
    id: mc.id,
    domain: mc.domain,
    category: mc.category,
    subcategory: mc.subcategory ?? undefined, // Convert null to undefined
    display_name: mc.display_name,
    default_color: mc.fill_color,
    display_order: mc.display_order,
    is_active: mc.is_active,
    is_system: mc.is_system,
    icon_svg: mc.icon_svg ?? undefined, // Convert null to undefined
    metadata: {},
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    // Extended fields for custom icon support
    icon_type: mc.icon_type,
    abbreviation: mc.abbreviation,
    fill_color: mc.fill_color,
    text_color: mc.text_color,
    icon_shape: mc.icon_shape,
  });

  // Build categoryTemplates array for backward compatibility
  const categoryTemplates: CategoryTemplate[] = (() => {
    if (productType === 'all') {
      const lighting = lightingCategories.map(convertToTemplate);
      const lv = lvCategories.map(convertToTemplate);
      const controls = controlsCategories.map(convertToTemplate);
      return [...lighting, ...lv, ...controls];
    }

    if (productType === 'lighting_controls') {
      return controlsCategories.map(convertToTemplate);
    }

    if (productType === 'lighting_fixtures') {
      return lightingCategories.map(convertToTemplate);
    }

    if (productType === 'lv_products') {
      return lvCategories.map(convertToTemplate);
    }

    return [];
  })();

  // Get parent categories for the create subcategory dialog
  const getParentCategoriesForDomain = (domain: DomainType): MergedCategory[] => {
    if (domain === 'lighting_fixtures') {
      return lightingHierarchy.parents;
    }
    if (domain === 'lv_products') {
      return lvHierarchy.parents;
    }
    return [];
  };

  logger.debug('[CategoryDropdown] Loaded categories:', {
    productType,
    lightingCount: lightingCategories.length,
    lvCount: lvCategories.length,
    totalTemplates: categoryTemplates.length,
  });

  // Parse value into category and subcategory
  useEffect(() => {
    if (typeof value === 'string') {
      // Legacy format - just category string
      setSelectedCategory(value);
      setSelectedSubcategory('');
    } else if (value && typeof value === 'object') {
      // New format - CategorySelection
      setSelectedCategory(value.category || '');
      setSelectedSubcategory(value.subcategory || '');
    } else {
      setSelectedCategory('');
      setSelectedSubcategory('');
    }
  }, [value]);

  // Get unique categories
  const categories = Array.from(new Set(categoryTemplates.map(t => t.category)));

  // Get subcategories for selected category
  const subcategories = selectedCategory
    ? categoryTemplates
        .filter(t => t.category === selectedCategory && t.subcategory)
        .map(t => t.subcategory!)
    : [];

  // Get template for current selection (subcategory if selected, else parent)
  const currentTemplate = categoryTemplates.find(
    t =>
      t.category === selectedCategory &&
      (selectedSubcategory ? t.subcategory === selectedSubcategory : !t.subcategory),
  );

  // Get parent category template (always the parent, regardless of subcategory selection)
  const parentCategoryTemplate = categoryTemplates.find(
    t => t.category === selectedCategory && !t.subcategory,
  );

  const handleCategoryChange = (newCategory: string) => {
    if (newCategory === '__clear__') {
      onChange(null);
      setSelectedCategory('');
      setSelectedSubcategory('');
    } else if (newCategory === '__create_new_lighting__') {
      // Open create category dialog for lighting
      setCreateDomain('lighting_fixtures');
      setCreateCategoryOpen(true);
    } else if (newCategory === '__create_new_lv__') {
      // Open create category dialog for LV/AV
      setCreateDomain('lv_products');
      setCreateCategoryOpen(true);
    } else {
      setSelectedCategory(newCategory);
      setSelectedSubcategory('');
      // Pass complete CategorySelection object
      onChange({
        category: newCategory,
        subcategory: undefined,
      });
    }
  };

  // Handler for when a new category is created
  const handleCategoryCreated = (newCategory: MergedCategory) => {
    logger.debug('[CategoryDropdown] Category created:', newCategory);
    // Invalidate queries to refresh lists
    queryClient.invalidateQueries({ queryKey: ['merged-categories'] });

    // Set the new category as selected
    setSelectedCategory(newCategory.category);
    setSelectedSubcategory('');
    onChange({
      category: newCategory.category,
      subcategory: undefined,
    });
  };

  // Handler for when a new subcategory is created
  const handleSubcategoryCreated = (newSubcategory: MergedCategory) => {
    logger.debug('[CategoryDropdown] Subcategory created:', newSubcategory);
    // Invalidate queries to refresh lists
    queryClient.invalidateQueries({ queryKey: ['merged-categories'] });

    // Set the new subcategory as selected
    if (newSubcategory.subcategory) {
      setSelectedSubcategory(newSubcategory.subcategory);
      onChange({
        category: selectedCategory,
        subcategory: newSubcategory.subcategory,
      });
    }
  };

  const handleSubcategoryChange = (newSubcategory: string) => {
    if (newSubcategory === '__none__') {
      setSelectedSubcategory('');
      if (selectedCategory) {
        onChange({
          category: selectedCategory,
          subcategory: undefined,
        });
      }
    } else if (newSubcategory === '__create_new_sub__') {
      // Determine domain from currently selected category
      const catTemplate = categoryTemplates.find(
        t => t.category === selectedCategory && !t.subcategory,
      );
      const domain = (catTemplate?.domain as DomainType) || 'lighting_fixtures';
      setCreateDomain(domain);
      setCreateSubcategoryOpen(true);
    } else {
      setSelectedSubcategory(newSubcategory);
      if (selectedCategory) {
        onChange({
          category: selectedCategory,
          subcategory: newSubcategory || undefined,
        });
      }
    }
  };

  return (
    <div
      className={cn('relative', layout === 'horizontal' && 'flex items-center gap-2', className)}
    >
      {/* Category Section */}
      <div className="flex-1">
        {showLabels && <label className="text-xs text-muted-foreground mb-1 block">Category</label>}
        <Select
          value={selectedCategory || undefined}
          onValueChange={handleCategoryChange}
          disabled={disabled}
        >
          <SelectTrigger
            className={cn(
              layout === 'horizontal' ? 'min-h-[40px]' : 'min-h-[48px]',
              effectiveDisplayMode === 'icon'
                ? 'w-[60px]'
                : layout === 'horizontal'
                  ? 'flex-1'
                  : 'w-full',
              'touch-manipulation',
              isAutoApplied && 'ring-2 ring-primary/30 bg-primary/5',
            )}
            title={
              effectiveDisplayMode === 'icon' && selectedCategory ? selectedCategory : undefined
            }
          >
            <SelectValue placeholder={effectiveDisplayMode === 'icon' ? '' : placeholder}>
              {selectedCategory && (
                <div className="flex items-center gap-2 justify-center">
                  <CategoryIconRenderer
                    category={selectedCategory}
                    template={parentCategoryTemplate}
                    size={20}
                    className="text-muted-foreground"
                  />
                  {effectiveDisplayMode !== 'icon' && (
                    <span className="text-sm font-medium">
                      {parentCategoryTemplate?.display_name || selectedCategory}
                    </span>
                  )}
                </div>
              )}
            </SelectValue>
          </SelectTrigger>

          <SelectContent className="max-h-[300px] overflow-y-auto" position="popper" sideOffset={4}>
            {/* Option to clear selection */}
            <SelectItem value="__clear__" className="min-h-[48px] cursor-pointer">
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="text-sm">No Category</span>
              </div>
            </SelectItem>

            {productType === 'all' ? (
              <>
                {/* Lighting Fixtures Group */}
                {(() => {
                  const lightingCategories = categories.filter(cat => {
                    const template = categoryTemplates.find(
                      t => t.category === cat && !t.subcategory,
                    );
                    return template?.domain === 'lighting_fixtures';
                  });

                  if (lightingCategories.length === 0) return null;

                  return (
                    <SelectGroup>
                      <SelectLabel className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
                        Lighting
                      </SelectLabel>
                      {lightingCategories.map(category => {
                        const template = categoryTemplates.find(
                          t => t.category === category && !t.subcategory,
                        );
                        return (
                          <SelectItem
                            key={category}
                            value={category}
                            className="min-h-[48px] cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <CategoryIconRenderer
                                category={category}
                                template={template}
                                size={20}
                                className="text-muted-foreground"
                              />
                              <span className="text-sm font-medium">
                                {template?.display_name || category}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  );
                })()}

                {/* Separator between groups */}
                {(() => {
                  const hasLighting = categories.some(cat => {
                    const template = categoryTemplates.find(
                      t => t.category === cat && !t.subcategory,
                    );
                    return template?.domain === 'lighting_fixtures';
                  });
                  const hasLV = categories.some(cat => {
                    const template = categoryTemplates.find(
                      t => t.category === cat && !t.subcategory,
                    );
                    return template?.domain === 'lv_products';
                  });
                  return hasLighting && hasLV ? <SelectSeparator /> : null;
                })()}

                {/* AV Products Group */}
                {(() => {
                  const lvCategories = categories.filter(cat => {
                    const template = categoryTemplates.find(
                      t => t.category === cat && !t.subcategory,
                    );
                    return template?.domain === 'lv_products';
                  });

                  if (lvCategories.length === 0) return null;

                  return (
                    <SelectGroup>
                      <SelectLabel className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
                        AV
                      </SelectLabel>
                      {lvCategories.map(category => {
                        const template = categoryTemplates.find(
                          t => t.category === category && !t.subcategory,
                        );
                        return (
                          <SelectItem
                            key={category}
                            value={category}
                            className="min-h-[48px] cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <CategoryIconRenderer
                                category={category}
                                template={template}
                                size={20}
                                className="text-muted-foreground"
                              />
                              <span className="text-sm font-medium">
                                {template?.display_name || category}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  );
                })()}

                {/* Separator between AV and Controls */}
                {(() => {
                  const hasAV = categories.some(cat => {
                    const template = categoryTemplates.find(
                      t => t.category === cat && !t.subcategory,
                    );
                    return template?.domain === 'lv_products';
                  });
                  const hasControls = categories.some(cat => {
                    const template = categoryTemplates.find(
                      t => t.category === cat && !t.subcategory,
                    );
                    return template?.domain === 'lighting_controls';
                  });
                  return hasAV && hasControls ? <SelectSeparator /> : null;
                })()}

                {/* Controls Group */}
                {(() => {
                  const controlCategories = categories.filter(cat => {
                    const template = categoryTemplates.find(
                      t => t.category === cat && !t.subcategory,
                    );
                    return template?.domain === 'lighting_controls';
                  });

                  if (controlCategories.length === 0) return null;

                  return (
                    <SelectGroup>
                      <SelectLabel className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
                        Controls
                      </SelectLabel>
                      {controlCategories.map(category => {
                        const template = categoryTemplates.find(
                          t => t.category === category && !t.subcategory,
                        );
                        return (
                          <SelectItem
                            key={category}
                            value={category}
                            className="min-h-[48px] cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <CategoryIconRenderer
                                category={category}
                                template={template}
                                size={20}
                                className="text-muted-foreground"
                              />
                              <span className="text-sm font-medium">
                                {template?.display_name || category}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectGroup>
                  );
                })()}

                {/* Create New Category Options - only show if category creation is allowed */}
                {canCreateCategory && (
                  <>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel className="text-xs font-semibold text-muted-foreground px-2 py-1.5">
                        Create New
                      </SelectLabel>
                      <SelectItem
                        value="__create_new_lighting__"
                        className="min-h-[48px] cursor-pointer text-primary"
                      >
                        <div className="flex items-center gap-3">
                          <Plus className="h-5 w-5" />
                          <span className="text-sm font-medium">New Lighting Category</span>
                        </div>
                      </SelectItem>
                      <SelectItem
                        value="__create_new_lv__"
                        className="min-h-[48px] cursor-pointer text-primary"
                      >
                        <div className="flex items-center gap-3">
                          <Plus className="h-5 w-5" />
                          <span className="text-sm font-medium">New AV Category</span>
                        </div>
                      </SelectItem>
                    </SelectGroup>
                  </>
                )}
              </>
            ) : (
              /* Single product type - no grouping needed */
              <>
                {categories.map(category => {
                  const template = categoryTemplates.find(
                    t => t.category === category && !t.subcategory,
                  );
                  return (
                    <SelectItem
                      key={category}
                      value={category}
                      className="min-h-[48px] cursor-pointer"
                    >
                      <div className="flex items-center gap-3">
                        <CategoryIconRenderer
                          category={category}
                          template={template}
                          size={20}
                          className="text-muted-foreground"
                        />
                        <span className="text-sm font-medium">
                          {template?.display_name || category}
                        </span>
                      </div>
                    </SelectItem>
                  );
                })}
                {/* Create New Category Option for single product type - only if allowed */}
                {canCreateCategory && (
                  <>
                    <SelectSeparator />
                    <SelectItem
                      value={
                        productType === 'lv_products'
                          ? '__create_new_lv__'
                          : '__create_new_lighting__'
                      }
                      className="min-h-[48px] cursor-pointer text-primary"
                    >
                      <div className="flex items-center gap-3">
                        <Plus className="h-5 w-5" />
                        <span className="text-sm font-medium">Create New Category</span>
                      </div>
                    </SelectItem>
                  </>
                )}
              </>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Subcategory Section - show when category selected (except Controls) */}
      {selectedCategory &&
        (() => {
          // Check if selected category supports subcategories (not Controls)
          const selectedCategoryDomain = categoryTemplates.find(
            t => t.category === selectedCategory && !t.subcategory,
          )?.domain;
          const supportsSubcategories = selectedCategoryDomain !== 'lighting_controls';
          // Show dropdown if there are existing subcategories OR category supports creating new ones
          return supportsSubcategories || subcategories.length > 0;
        })() && (
          <div className="flex-1">
            {showLabels && (
              <label className="text-xs text-muted-foreground mb-1 block">Subcategory</label>
            )}
            <Select
              value={selectedSubcategory || undefined}
              onValueChange={handleSubcategoryChange}
              disabled={disabled}
            >
              <SelectTrigger
                className={cn(
                  layout === 'horizontal' ? 'min-h-[40px]' : 'min-h-[48px]',
                  effectiveDisplayMode === 'icon'
                    ? 'w-[60px]'
                    : layout === 'horizontal'
                      ? 'flex-1'
                      : 'w-full mt-2',
                )}
                title={
                  effectiveDisplayMode === 'icon' && selectedSubcategory
                    ? selectedSubcategory
                    : undefined
                }
              >
                <SelectValue placeholder={effectiveDisplayMode === 'icon' ? '' : 'Subcategory'}>
                  {selectedSubcategory && (
                    <div className="flex items-center gap-2 justify-center">
                      {(() => {
                        const subcategoryTemplate = categoryTemplates.find(
                          t =>
                            t.category === selectedCategory &&
                            t.subcategory === selectedSubcategory,
                        );
                        const parentTemplate = categoryTemplates.find(
                          t => t.category === selectedCategory && !t.subcategory,
                        );
                        const templateToUse = subcategoryTemplate || parentTemplate;
                        const isInherited = !subcategoryTemplate && parentTemplate;

                        return (
                          <div className={cn(isInherited && 'opacity-75')}>
                            <CategoryIconRenderer
                              category={selectedCategory}
                              template={templateToUse}
                              size={20}
                              className="text-muted-foreground"
                            />
                          </div>
                        );
                      })()}
                      {effectiveDisplayMode !== 'icon' && (
                        <span className="text-sm font-medium">{selectedSubcategory}</span>
                      )}
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {subcategories.map(subcategory => {
                  const subcategoryTemplate = categoryTemplates.find(
                    t => t.category === selectedCategory && t.subcategory === subcategory,
                  );
                  const parentTemplate = categoryTemplates.find(
                    t => t.category === selectedCategory && !t.subcategory,
                  );

                  // Use subcategory icon if available, otherwise use parent with opacity
                  const templateToUse = subcategoryTemplate || parentTemplate;
                  const isInherited = !subcategoryTemplate && parentTemplate;

                  return (
                    <SelectItem key={subcategory} value={subcategory}>
                      <div className="flex items-center gap-2">
                        <div className={cn(isInherited && 'opacity-75')}>
                          <CategoryIconRenderer
                            category={selectedCategory}
                            template={templateToUse}
                            size={16}
                            className="text-muted-foreground"
                          />
                        </div>
                        <span className="text-sm">{subcategory}</span>
                      </div>
                    </SelectItem>
                  );
                })}
                {/* Create New Subcategory Option - only if allowed */}
                {canCreateSubcategory && (
                  <>
                    <SelectSeparator />
                    <SelectItem value="__create_new_sub__" className="cursor-pointer text-primary">
                      <div className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        <span className="text-sm font-medium">Create New Subcategory</span>
                      </div>
                    </SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        )}

      {isAutoApplied && (
        <Badge
          variant="secondary"
          className="absolute -top-2 -right-2 text-xs z-10 bg-primary text-primary-foreground"
        >
          Auto
        </Badge>
      )}

      {/* Create Category Dialog */}
      <QuickCategoryCreateDialog
        open={createCategoryOpen}
        onOpenChange={setCreateCategoryOpen}
        domain={createDomain}
        onCategoryCreated={handleCategoryCreated}
        isSubcategory={false}
      />

      {/* Create Subcategory Dialog */}
      <QuickCategoryCreateDialog
        open={createSubcategoryOpen}
        onOpenChange={setCreateSubcategoryOpen}
        domain={createDomain}
        onCategoryCreated={handleSubcategoryCreated}
        isSubcategory={true}
        parentCategory={selectedCategory}
        parentCategories={getParentCategoriesForDomain(createDomain)}
      />
    </div>
  );
}
